import {
  PLUGIN_STEP_TEMPORAL_OPTION_KEYS,
  type PluginStepTemporalOptionKey
} from '@insio/shared-types';
import { Input } from './inputs';
import { InputWrapper } from './InputWrapper';
import { cn } from '../utils';

export type StepTemporalOverridesValue = Record<
  string,
  Partial<Record<PluginStepTemporalOptionKey, number>>
>;

export interface StepTemporalEditorStep {
  name: string;
  description?: string;
  /** Declared step defaults — shown as placeholders behind the override inputs. */
  temporalDefaults?: Partial<Record<PluginStepTemporalOptionKey, number>> | null;
}

const defaultLabels: Record<PluginStepTemporalOptionKey, string> = {
  startToCloseTimeoutMs: 'Timeout (ms)',
  maximumAttempts: 'Max attempts',
  initialIntervalMs: 'Retry interval (ms)',
  backoffCoefficient: 'Backoff coefficient'
};

export interface StepTemporalOverridesEditorProps {
  /** Declared steps in definition order. */
  steps: readonly StepTemporalEditorStep[];
  value: Readonly<StepTemporalOverridesValue>;
  /** Field labels keyed by option — pass translated strings in localized apps. */
  labels?: Record<PluginStepTemporalOptionKey, string>;
  /** Keep step entries whose last option was cleared instead of pruning them. */
  isEmptyStepKept?: boolean;
  className?: string;
  testId?: string;
  onChange(next: StepTemporalOverridesValue): void;
}

/** Per-step Temporal override inputs — an empty input clears the option (step default applies). */
export function StepTemporalOverridesEditor({
  steps,
  value,
  labels = defaultLabels,
  isEmptyStepKept = false,
  className,
  testId,
  onChange
}: StepTemporalOverridesEditorProps) {
  if (steps.length === 0) {
    return null;
  }

  const setOption = (stepName: string, option: PluginStepTemporalOptionKey, raw: string): void => {
    const stepOverride = { ...value[stepName] };
    const parsed = Number(raw);
    if (raw.trim() === '' || Number.isNaN(parsed)) {
      delete stepOverride[option];
    } else {
      stepOverride[option] = parsed;
    }
    const next = { ...value };
    if (Object.keys(stepOverride).length === 0 && !isEmptyStepKept) {
      delete next[stepName];
    } else {
      next[stepName] = stepOverride;
    }
    onChange(next);
  };

  return (
    <div data-testid={testId} className={cn('flex flex-col gap-sm', className)}>
      {steps.map(step => (
        <div
          key={step.name}
          className='flex flex-col gap-xs rounded-md border border-border-base p-sm'
        >
          <p className='text-sm font-medium text-foreground-base'>{step.name}</p>
          {step.description !== undefined && step.description !== '' && (
            <p className='text-xs text-foreground-muted'>{step.description}</p>
          )}
          <div className='grid grid-cols-4 gap-sm'>
            {PLUGIN_STEP_TEMPORAL_OPTION_KEYS.map(option => (
              <InputWrapper key={option} label={labels[option]} className='gap-xs'>
                <Input
                  type='number'
                  value={value[step.name]?.[option] ?? ''}
                  placeholder={
                    typeof step.temporalDefaults?.[option] === 'number'
                      ? String(step.temporalDefaults[option])
                      : ''
                  }
                  onChange={event => setOption(step.name, option, event.target.value)}
                />
              </InputWrapper>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

StepTemporalOverridesEditor.displayName = 'StepTemporalOverridesEditor';
